import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { take, tap } from 'rxjs/operators';

import { environment } from '@customer-portal/environments';

import { AuthTokenConstants } from '../../constants/auth-constants';
import { AuthServiceResponse } from '../../models';
import { CoBrowsingCookieService } from '../co-browsing';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly authApiUrl = environment.authApiUrl;
  private isLoggingOut = signal<boolean>(this.getLogoutStateFromStorage());
  private readonly logoutSubject = new Subject<void>();

  readonly logout$ = this.logoutSubject.asObservable();

  coBrowsingCookieService = inject(CoBrowsingCookieService);

  constructor(private readonly http: HttpClient) {}

  login(): void {
    this.clearTokenData();
    this.setLogoutState(false);

    const bypassToken = this.getBypassToken();
    const returnUrl = bypassToken
      ? `${environment.baseUrl}?bypass=${bypassToken}#idp`
      : `${environment.baseUrl}#idp`;

    const params = new URLSearchParams({
      returnUrl,
    });

    window.location.href = `${this.authApiUrl}/login?${params.toString()}`;
  }

  logout(): Observable<string> {
    this.clearTokenData();
    this.setLogoutState(true);
    this.coBrowsingCookieService
      .postUserEmailCookie(null)
      .pipe(take(1))
      .subscribe();

    return this.http
      .post<string>(
        `${this.authApiUrl}/Logout`,
        {},
        { responseType: 'text' as 'json', withCredentials: true },
      )
      .pipe(tap(() => this.logoutSubject.next()));
  }

  isLogOutInProgress(): boolean {
    return this.isLoggingOut();
  }

  getToken(): Observable<string> {
    return this.http.get(`${this.authApiUrl}/GetAccessToken`, {
      responseType: 'text',
      withCredentials: true,
    });
  }

  getClientCredentialToken(): Observable<string> {
    return this.http.get<string>(`${this.authApiUrl}/Status`);
  }

  isUserAuthenticatedWithExpiryInfo(): Observable<AuthServiceResponse> {
    if (this.isLoggingOut()) {
      return of({
        isUserAuthenticated: false,
        expiryTimeUtc: new Date(),
      });
    }

    return this.http.get<AuthServiceResponse>(
      `${this.authApiUrl}/UserAuthenticatedwithExpiry`,
      {
        withCredentials: true,
      },
    );
  }

  isUserValidated(): Observable<boolean> {
    return this.http.get<boolean>(`${this.authApiUrl}/ValidateUser`);
  }

  resetLogoutState(): void {
    this.setLogoutState(false);
  }

  storeTokenData(expiresAt: string): void {
    localStorage.setItem(AuthTokenConstants.TOKEN_EXPIRY_KEY, expiresAt);
  }

  clearTokenData(): void {
    localStorage.removeItem(AuthTokenConstants.SHOW_MODAL_KEY);
    localStorage.removeItem(AuthTokenConstants.TOKEN_EXPIRY_KEY);
    localStorage.removeItem(AuthTokenConstants.LAST_ACTIVITY_KEY);
    localStorage.removeItem(AuthTokenConstants.TOKEN_DURATION_KEY);
    localStorage.removeItem(AuthTokenConstants.AUTH_LOGGING_OUT);
    localStorage.removeItem(AuthTokenConstants.CO_BROWSING_EMAIL);
  }

  public getBypassToken(): string | null {
    const isBypassed = sessionStorage.getItem('maintenance_bypass') === 'true';

    if (isBypassed) {
      const storedToken = sessionStorage.getItem('maintenance_bypass_token');

      if (storedToken) {
        return storedToken;
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('bypass');

    return urlToken || null;
  }

  private setLogoutState(value: boolean): void {
    this.isLoggingOut.set(value);
    localStorage.setItem(
      AuthTokenConstants.AUTH_LOGGING_OUT,
      JSON.stringify(value),
    );
  }

  private getLogoutStateFromStorage(): boolean {
    const stored = localStorage.getItem(AuthTokenConstants.AUTH_LOGGING_OUT);

    return stored ? JSON.parse(stored) : false;
  }
}
